import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faIdBadge, faExclamationTriangle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { HR_NAV } from './nav';

const Credentialing: React.FC = () => {
    const { api } = useInternalApi();
    const [expired, setExpired] = useState<any[]>([]);
    const [expiring, setExpiring] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>(null);
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => { document.title = 'Credentialing & Licences - Uzima Internal'; }, []);
    useEffect(() => {
        api.get('hr-api/credentialing/').then(r => {
            if (r.data?.status === 'success') {
                setExpired(r.data.data.expired || []);
                setExpiring(r.data.data.expiring_within_90_days || []);
                setSummary(r.data.data.summary);
                setNote(r.data.data.note || '');
            }
        }).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const Table = ({ rows, overdue }: { rows: any[]; overdue: boolean }) => (
        <table className="table table-hover align-middle mb-0">
            <thead className="table-light"><tr><th>Holder</th><th>Credential</th><th>Reference</th><th>Expires</th><th>{overdue ? 'Days overdue' : 'Days left'}</th></tr></thead>
            <tbody>
                {rows.map((r, i) => (
                    <tr key={i}>
                        <td><strong>{r.name || '—'}</strong><div className="small text-muted">{r.kind.replace(/_/g, ' ')}</div></td>
                        <td className="small">{r.detail || '—'}</td>
                        <td className="small">{r.reference || '—'}</td>
                        <td className="small">{r.expires_on}</td>
                        <td><span className={`badge bg-${overdue ? 'danger' : r.days <= 30 ? 'warning' : 'secondary'}`}>{Math.abs(r.days)}</span></td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    return (
        <InternalShell activeDashboard="human_resources" subNav={HR_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faIdBadge} className="me-2 text-primary" />Credentialing & Licence Expiry</h2>
            <p className="text-muted mb-4">Clinical credentials that have expired or are close to it</p>

            <div className="alert alert-warning small"><FontAwesomeIcon icon={faInfoCircle} className="me-2" />{note}</div>

            {loading || !summary ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <>
                    <div className="row mb-4">
                        <div className="col-md-3 mb-3">
                            <div className={`card border-0 shadow-sm ${summary.expired_count ? 'border-danger' : ''}`}><div className="card-body">
                                <div className="text-muted small mb-1">Expired</div>
                                <h3 className={summary.expired_count ? 'text-danger' : 'text-success'}>{summary.expired_count}</h3>
                            </div></div>
                        </div>
                        <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                            <div className="text-muted small mb-1">Expiring in 90 days</div>
                            <h3 className={summary.expiring_count ? 'text-warning' : ''}>{summary.expiring_count}</h3>
                        </div></div></div>
                        <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                            <div className="text-muted small mb-1">Practitioner documents</div><h4>{summary.practitioner_documents}</h4>
                        </div></div></div>
                        <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                            <div className="text-muted small mb-1">Clinic documents</div><h4>{summary.clinic_documents}</h4>
                        </div></div></div>
                    </div>

                    {expired.length > 0 && (
                        <div className="alert alert-danger">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                            <strong>{expired.length} credential(s) have expired.</strong> Practising on an expired licence is a clinical governance issue, not an administrative one.
                        </div>
                    )}

                    <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header bg-white"><strong className="text-danger">Expired</strong></div>
                        <div className="card-body p-0">
                            {expired.length === 0
                                ? <div className="text-center py-4 text-muted">Nothing expired.</div>
                                : <Table rows={expired} overdue />}
                        </div>
                    </div>

                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white"><strong className="text-warning">Expiring within 90 days</strong></div>
                        <div className="card-body p-0">
                            {expiring.length === 0
                                ? <div className="text-center py-4 text-muted">Nothing expiring in the next 90 days.</div>
                                : <Table rows={expiring} overdue={false} />}
                        </div>
                    </div>
                </>
            )}
        </InternalShell>
    );
};

export default Credentialing;
